import React from 'react';
import {loadConcord} from '../utils/loadConcord';
import {nodeId} from '../../../libs/react-widgets';

/**
 * Concord.tech cookie banner provider
 * Requires cookieBanner.provider === 'concord' and cookieBanner.config.domainId
 */
export const concordProvider = {
  /**
   * Default trigger element ID for Concord.tech
   */
  defaultTriggerId: 'concordTrigger',

  /**
   * Checks if Concord provider should be used based on settings
   * @param {Object} settings - Application settings
   * @returns {boolean}
   */
  shouldUse(settings) {
    return !!(settings.cookieBanner?.provider === 'concord' && settings.cookieBanner?.config?.domainId);
  },

  /**
   * Checks if initialization is needed (settings changed)
   * @param {Object} settings - Current settings
   * @param {Object} prevSettings - Previous settings
   * @returns {boolean}
   */
  shouldInitialize(settings, prevSettings) {
    const currentConfig = settings.cookieBanner?.config;
    const prevConfig = prevSettings?.cookieBanner?.config;
    return !!(currentConfig && JSON.stringify(currentConfig) !== JSON.stringify(prevConfig));
  },

  /**
   * Initializes Concord.tech script
   * @param {Object} settings - Application settings
   * @param {Function} onError - Error callback (message, error)
   * @returns {Promise}
   */
  initialize(settings, onError) {
    const domainId = settings.cookieBanner?.config?.domainId;

    if (!domainId) {
      return Promise.reject(new Error('Concord domainId not found in settings'));
    }

    return loadConcord(domainId).catch((e) => {
      onError('Error loading Concord.', e);
      throw e;
    });
  },

  /**
   * Attaches click handler to trigger element (idempotent)
   * @param {string} triggerId - Element ID to attach handler to
   * @param {Function} onError - Error callback (message, error)
   */
  attachHandler(triggerId, onError) {
    const element = document.getElementById(triggerId || this.defaultTriggerId);
    if (!element) {
      return;
    }

    // Prevent duplicate handlers
    if (element.dataset.handlerAttached === 'true') {
      return;
    }

    element.onclick = (e) => {
      e.preventDefault();
      if (window.Concord && typeof window.Concord.open === 'function') {
        window.Concord.open();
      } else {
        onError('Concord not ready.', new Error('Concord.open not available'));
      }
    };
    element.dataset.handlerAttached = 'true';
  },

  /**
   * Returns React style elements for Concord.tech
   * @param {Object} settings - Application settings
   * @returns {Array<React.Element>}
   */
  getStyles(settings) {
    // TODO: Implement Concord.tech styles
    const concordStyles = settings.styles?.cookieBanner?.concord;
    if (!concordStyles) {
      return [];
    }

    const styles = [];

    // Add styles based on concordStyles configuration
    // Example implementation:
    // if (concordStyles.fontSize) {
    //   styles.push(
    //     <style key={nodeId()}>{`
    //       .concord-banner {
    //         font-size: ${concordStyles.fontSize};
    //       }
    //     `}</style>
    //   );
    // }

    return styles;
  }
};
